import { type CSSProperties, type FC, type Ref, useEffect } from 'react';
import { differenceInSeconds } from 'date-fns';

import { Backdrop } from '@rikstv/play-common/src/components/backdrop/Backdrop';
import { usePinStore } from '@rikstv/play-common/src/components/pin/usePinStore';
import { StackedProviderImage } from '@rikstv/play-common/src/components/providerImage/StackedProviderImage';
import { ClockIcon, InfoCircleIcon } from '@rikstv/play-common/src/icons';
import { readableDuration } from '@rikstv/play-common/src/utils/date/date.i18n.utils';
import { getRatioOfCurrentTimeInRange, isDateBetweenDates } from '@rikstv/play-common/src/utils/date/date.utils';
import { middleDot } from '@rikstv/play-common/src/utils/special-characters/special-characters.utils';
import { addQueryParams } from '@rikstv/play-common/src/utils/url/url.utils';
import { useTrailerPlayerStore } from '@rikstv/play-player/src/components/TrailerPlayer/useTrailerPlayerStore';
import { Body, Bold, Gutter, H1, Meta, SubBody } from '@rikstv/shared-components';

import { DurationBar } from '../../../components/durationBar/DurationBar';
import { ImdbBadge } from '../../../components/imdbBadge/ImdbBadge';
import { ContentStackMetadata, MessageObject } from '../contentStack.types';

import { ParentSeriesLink } from './series/ParentSeriesLink';
import { MediaButtonList, MediaMetaProps } from './MediaButtonList';

import styles from './MediaMetadata.module.css';

export const MediaMetadata: FC<MediaMetaProps> = props => {
  const { media } = props;
  const progress = getLiveOrUserProgress(media.defaultProviderVariant);
  const setTrailerSources = useTrailerPlayerStore(s => s.setTrailerSources);
  const { assetIsAgeRestricted } = usePinStore();
  const isAgeRestricted = assetIsAgeRestricted(media.metadata.ageLimit);

  useEffect(() => {
    setTrailerSources(media.metadata.trailers);

    return () => {
      // reset trailer sources on unmount
      setTrailerSources(undefined);
    };
  }, [media.metadata.trailers, setTrailerSources]);

  return (
    <section className={styles.heroContainer}>
      {media.images.backdropImage && (
        <Backdrop href={media.images.backdropImage.href} isAgeRestricted={isAgeRestricted} />
      )}
      <div className={styles.mediaInfoWrapper}>
        <Gutter>
          <div className={styles.mediaInfoContainer}>
            <MediaPoster {...media} />
            <div className={styles.mediaInfoContentWrapper}>
              <div className={styles.mediaInfoContent}>
                <MediaTitle {...media} />
                <LinearData {...media} />
                <ClampedDescription text={media.metadata.description} />
                {media.messages && <Message messages={media.messages} />}
                {progress && <ProgressInfo progressData={progress} />}
              </div>

              <MediaButtonList {...props} />
            </div>
          </div>
          <ParentSeriesLink media={media} />
        </Gutter>
        <StackedProviderImage
          width={'min(10vw, 64px)'}
          className={styles.mediaProviderLogos}
          variants={media.providerLogos.map(({ href, name }) => ({ name, logo: href }))}
        />
      </div>
    </section>
  );
};

const MediaPoster = (media: ContentStackMetadata) => {
  if (!media.images.posterImage) {
    return null;
  }

  //Add padding-top if we don't have keyart to align with title
  const paddingTop: CSSProperties | undefined = !media.images.keyArtLeft
    ? { paddingTop: 'var(--rds-spacing--8)' }
    : undefined;

  return <img style={paddingTop} className={styles.poster} alt="" src={media.images.posterImage.href + '?width=200'} />;
};

const MediaTitle = (media: ContentStackMetadata) => (
  <>
    <H1 className={media.images.keyArtLeft ? 'sr-only' : ''}>{media.metadata.title}</H1>
    {media.images.keyArtLeft && (
      <img
        aria-hidden
        title={media.metadata.title}
        className={styles.keyArt}
        alt={''}
        src={addQueryParams(media.images.keyArtLeft.href, {
          lossless: 'true',
          height: '125',
        })}></img>
    )}
    <Meta className={styles.metadata}>
      <span>
        {media.metadata.subtitle}
        {media.metadata.imdbRating ? ` ${middleDot}` : ''}
      </span>
      <ImdbBadge imdbRating={media.metadata.imdbRating} />
    </Meta>
  </>
);

const LinearData = ({ defaultProviderVariant, type }: ContentStackMetadata) => {
  //lineardata is used on both episode and series page but state should not be displayed for series
  if (!defaultProviderVariant?.state || defaultProviderVariant.linearData == null || type === 'series') {
    return null;
  }

  return (
    <div className={styles.linearDataLabel}>
      <ClockIcon />
      <SubBody>{defaultProviderVariant.linearData.label}</SubBody>
    </div>
  );
};

const ProgressInfo: FC<{
  progressData: { progressLabel?: string; progressRatio?: number; title?: string };
}> = ({ progressData: { progressLabel, progressRatio, title } }) => {
  const progress = progressRatio ? Math.round(progressRatio * 100) : undefined;

  if (!progress) {
    return null;
  }

  return (
    <div className={styles.progressContainer}>
      {title && <Bold>{title}</Bold>}
      {progress != null && <DurationBar percent={progress} className={styles.progressBar} />}
      {Boolean(progressLabel) && <Body>{progressLabel}</Body>}
    </div>
  );
};

/**
 * Clamps description to x lines
 */
const ClampedDescription: FC<{ text: string; nrOfLines?: number }> = ({ text, nrOfLines = 3 }) => {
  // Remove "les mer" label if height is sufficient for content
  const pRef: Ref<HTMLElement> = pTag => {
    if (pTag) {
      const height = pTag.getBoundingClientRect().height;
      const hideExpandLabel = Math.abs(pTag.scrollHeight - height) < 16; // ~1rem
      if (hideExpandLabel) {
        pTag.dataset.expanded = 'true';
      }
    }
  };

  return (
    <div className={styles.clampedDescription} style={{ ['--nr-of-lines']: nrOfLines.toString() } as CSSProperties}>
      <input type="checkbox" id="expanded" />
      <Body ref={pRef}>{text}</Body>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
      <label htmlFor="expanded" role="button">
        Les mer
      </label>
    </div>
  );
};

//In the future there should be several icons
const MessageIconMap: Record<string, FC<{ style?: CSSProperties }>> = {
  Information: InfoCircleIcon,
};

const Message: FC<{ messages: MessageObject[] }> = ({ messages }) => {
  return messages.map((message, idx) => {
    const MessageIcon = MessageIconMap[message.type];

    return (
      <Bold key={idx} className={styles.mediaInfoMessage}>
        {MessageIcon && <MessageIcon style={{ height: '24px', width: '24px' }} />}
        {message.value}
      </Bold>
    );
  });
};

function getLiveOrUserProgress(variant: ContentStackMetadata['defaultProviderVariant']) {
  if (!variant || variant?.behavior === 'ShowVariants') {
    return undefined;
  }

  if (variant.linearData) {
    const { linearStart, linearEnd } = variant.linearData;
    if (isDateBetweenDates(new Date(), linearStart, linearEnd)) {
      const remainingDurationInSeconds = differenceInSeconds(new Date(linearEnd), new Date());

      const progress = {
        progressRatio: getRatioOfCurrentTimeInRange(linearStart, linearEnd),
        progressLabel: `${readableDuration(remainingDurationInSeconds)} igjen`,
        title: variant.episodeTitle,
      };

      return progress;
    }
  }

  return { ...variant.progressData, title: variant.episodeTitle };
}
